/**
 * Shared Auto-send to Xero copy + toast helpers.
 * One venue flag (`automaticSendToXero`) — keep wording consistent everywhere.
 *
 * Post-reconcile toast matrix (5e + A1):
 * - sent → short success
 * - skipped (no defaults / expired / not connected) → warning
 * - odd skips when ON (order not found / missing org / missing supplier) → warning
 * - API fail → soft fail (reconcile OK)
 * - auto-send off, supplier off, or already sent → quiet
 */

import { notification } from 'antd'

export const XERO_AUTO_SEND_TOGGLE_LABEL = 'Auto-send to Xero'

/** Single venue field — Integrations, GR, and Invoices all read/write this. */
export const XERO_AUTO_SEND_VENUE_FIELD = 'automaticSendToXero'

export function buildXeroAutoSendVenueUpdate(enabled) {
  return { [XERO_AUTO_SEND_VENUE_FIELD]: Boolean(enabled) }
}

export function readXeroAutoSendEnabled(venue = {}) {
  return Boolean(venue?.[XERO_AUTO_SEND_VENUE_FIELD])
}

/** Same field name on VenueSupplier — missing/undefined means ON (opt-out). */
export const XERO_AUTO_SEND_SUPPLIER_FIELD = 'automaticSendToXero'

export const XERO_AUTO_SEND_SUPPLIER_TOGGLE_LABEL =
  'Auto-send for this supplier'

export const XERO_AUTO_SEND_SUPPLIER_TOGGLE_HINT =
  'When Auto-send to Xero is ON, we send reconciled bills for this supplier. Turn off to exclude them.'

export function readXeroAutoSendEnabledForSupplier(venueSupplier = {}) {
  return venueSupplier?.[XERO_AUTO_SEND_SUPPLIER_FIELD] !== false
}

export function buildXeroAutoSendSupplierUpdate(enabled) {
  return { [XERO_AUTO_SEND_SUPPLIER_FIELD]: Boolean(enabled) }
}

export const XERO_AUTO_SEND_BODY =
  'When on, OMS sends the bill to Xero after an invoice is reconciled (for example Match & reconcile), using each supplier’s default Xero account and contact. When off, use the Send to Xero (paper-plane) action on Goods Receipting once the order is Confirmed.'

/** GR settings one-liner — fallback when auto-send is ON (body already covers OFF). */
export const XERO_AUTO_SEND_MANUAL_SEND_HINT =
  'Manual send: paper-plane on a Confirmed order.'

/**
 * Show the short paper-plane tip on GR when auto-send is ON.
 * When OFF, XERO_AUTO_SEND_BODY already spells out the manual path.
 */
export function shouldShowXeroManualSendHint({
  autoSendEnabled,
  xeroConnected,
  xeroExpired,
} = {}) {
  if (!autoSendEnabled) return false
  if (!xeroConnected || xeroExpired) return false
  return true
}

export const XERO_AUTO_SEND_SUPPLIER_HINT =
  'Set each supplier’s default Xero account and contact under Your Suppliers. Without those defaults, OMS will not send — map the supplier, then use Send to Xero manually (or reconcile again after mapping).'

export const XERO_AUTO_SEND_REQUIRES_CONNECTION =
  'Connect Xero under Integrations first, then use Send to Xero if needed.'

export const XERO_AUTO_SEND_REQUIRES_RECONNECT =
  'Your Xero connection has expired. Reconnect under Integrations, then try again.'

/** Shown on Integrations itself (connect/reconnect is above the toggle). */
export const XERO_AUTO_SEND_CONNECT_ON_INTEGRATIONS =
  'Connect Xero above before turning auto-send on.'

export const XERO_AUTO_SEND_RECONNECT_ON_INTEGRATIONS =
  'Your Xero connection has expired. Reconnect above before auto-send can run.'

export const XERO_AUTO_SEND_SUCCESS_MESSAGE = 'Reconciled and sent to Xero.'

export const XERO_AUTO_SEND_ATTACHED_SUCCESS_MESSAGE =
  'Attached and sent to Xero.'

export const XERO_AUTO_SEND_PARTIAL_WAIT_COPY =
  'Xero send waits until the invoice is fully reconciled.'

export const XERO_AUTO_SEND_MISSING_DEFAULTS =
  'This supplier has no default Xero account and contact mapped. Map them under Your Suppliers, then use Send to Xero on Goods Receipting.'

export const XERO_AUTO_SEND_MISSING_FALLBACK_TIP =
  'Tip: set a fallback purchases account under Integrations for Force send when a mapped contact fails.'

export const XERO_AUTO_SEND_FAILURE_RETRY =
  'The invoice was reconciled, but sending the bill to Xero failed. Use the Send to Xero (paper-plane) action on Goods Receipting to retry.'

/** Odd skips — auto-send ON but send could not start (previously silent). */
export const XERO_AUTO_SEND_ORDER_NOT_FOUND =
  'We couldn’t find the purchase order to send. Use the Send to Xero (paper-plane) action on Goods Receipting, or check Integrations.'

export const XERO_AUTO_SEND_MISSING_ORGANIZATION =
  'Xero is connected but no organisation is available to send to. Check Integrations, then use Send to Xero on Goods Receipting.'

export const XERO_AUTO_SEND_MISSING_SUPPLIER =
  'The order is missing a supplier link needed for Xero. Fix the supplier on Goods Receipting, then use Send to Xero (paper-plane).'

export const XERO_AUTO_SEND_MISSING_ORDER_PAYLOAD =
  'We couldn’t build the Xero bill from this order. Use the Send to Xero (paper-plane) action on Goods Receipting to retry.'

/** Reasons that should warn when venue auto-send is ON (A1). */
export const XERO_AUTO_SEND_ODD_SKIP_REASONS = new Set([
  'order_not_found',
  'missing_organization',
  'missing_supplier',
  'missing_order',
])

/**
 * Intentional quiet skips (A2) — paper-plane / banner cover these; no toast noise.
 * Venue OFF is checked separately via readXeroAutoSendEnabled(venue).
 */
export const XERO_AUTO_SEND_QUIET_SKIP_REASONS = new Set([
  'disabled',
  'supplier_disabled',
  'already_sent',
])

export function isXeroAutoSendQuietSkip({
  venue = {},
  order = {},
  skipReason,
} = {}) {
  if (!readXeroAutoSendEnabled(venue)) return true
  if (order?.sentToXero) return true
  if (XERO_AUTO_SEND_QUIET_SKIP_REASONS.has(skipReason)) return true
  return false
}

/**
 * Soft warning when auto-send is ON but send could not start for an “odd”
 * reason that used to stay silent (order miss / missing org / missing supplier).
 * Quiet cases (OFF / supplier OFF / already sent) are handled by the caller.
 */
export function getXeroAutoSendOddSkipNotice(
  skipReason,
  completedActionLabel = 'Reconciled',
) {
  if (!XERO_AUTO_SEND_ODD_SKIP_REASONS.has(skipReason)) return null

  const title = `${completedActionLabel} — not sent to Xero`
  const descriptions = {
    order_not_found: XERO_AUTO_SEND_ORDER_NOT_FOUND,
    missing_organization: XERO_AUTO_SEND_MISSING_ORGANIZATION,
    missing_supplier: XERO_AUTO_SEND_MISSING_SUPPLIER,
    missing_order: XERO_AUTO_SEND_MISSING_ORDER_PAYLOAD,
  }

  return {
    message: title,
    description: descriptions[skipReason],
  }
}

export function buildVenueSuppliersPath(venueId) {
  if (!venueId) return '/suppliers'
  return `/v/${venueId}/suppliers`
}

export function buildIntegrationsPath(venueId) {
  if (!venueId) return '/integrations'
  return `/v/${venueId}/integrations`
}

/**
 * Soft guidance after reconcile/attach when auto-send could not run.
 * Reconcile/attach itself should still succeed — these are warnings, not hard errors.
 *
 * Quiet when auto-send is OFF or the order was already sent.
 *
 * @param {object} opts
 * @param {string} [opts.completedActionLabel='Reconciled'] - e.g. 'Reconciled' or 'Attached'
 */
export function getXeroAutoSendSkipNotice({
  autoSendEnabled,
  xeroConnected,
  xeroExpired,
  hasSupplierDefaults,
  alreadySentToXero,
  completedActionLabel = 'Reconciled',
} = {}) {
  if (!autoSendEnabled) return null
  if (alreadySentToXero) return null

  const title = `${completedActionLabel} — not sent to Xero`

  if (xeroExpired) {
    return {
      message: title,
      description: XERO_AUTO_SEND_REQUIRES_RECONNECT,
      action: 'reconnect',
    }
  }

  if (!xeroConnected) {
    return {
      message: title,
      description: XERO_AUTO_SEND_REQUIRES_CONNECTION,
      action: 'reconnect',
    }
  }

  if (!hasSupplierDefaults) {
    return {
      message: title,
      description: XERO_AUTO_SEND_MISSING_DEFAULTS,
      action: 'map',
    }
  }

  return null
}

/** Toast action helpers for Map / Reconnect / Send anyway CTAs. */
export function getXeroToastActionLabel(action) {
  if (action === 'map') return 'Map suppliers'
  if (action === 'reconnect') return 'Reconnect'
  if (action === 'send_anyway') return 'Send anyway'
  return null
}

export function getXeroAutoSendSuccessNotice(
  completedActionLabel = 'Reconciled',
) {
  return {
    message:
      completedActionLabel === 'Attached'
        ? XERO_AUTO_SEND_ATTACHED_SUCCESS_MESSAGE
        : XERO_AUTO_SEND_SUCCESS_MESSAGE,
  }
}

/**
 * Gate messaging for the Integrations primary auto-send control.
 * Toggle should stay visible but disabled until connected (and not expired).
 */
export function getXeroAutoSendIntegrationsGate({
  xeroConnected,
  xeroExpired,
} = {}) {
  if (xeroExpired) {
    return {
      disabled: true,
      tone: 'warning',
      message: XERO_AUTO_SEND_RECONNECT_ON_INTEGRATIONS,
    }
  }
  if (!xeroConnected) {
    return {
      disabled: true,
      tone: 'info',
      message: XERO_AUTO_SEND_CONNECT_ON_INTEGRATIONS,
    }
  }
  return {
    disabled: false,
    tone: null,
    message: null,
  }
}

/** A supplier can only auto-send when both Xero defaults are mapped. */
export function hasXeroSupplierDefaults(venueSupplier = {}) {
  return Boolean(
    String(venueSupplier?.defaultAccount || '').trim() &&
      String(venueSupplier?.defaultContact || '').trim(),
  )
}

export function countSuppliersWithXeroDefaults(venueSuppliers = []) {
  if (!Array.isArray(venueSuppliers)) return 0
  return venueSuppliers.filter(hasXeroSupplierDefaults).length
}

export const XERO_BANNER_STATE = {
  EXPIRED: 'expired',
  NO_SUPPLIER_DEFAULTS: 'no_supplier_defaults',
  OFF: 'off',
  ON: 'on',
}

/** Primary CTA on Integrations when auto-send is ON but nothing is mapped (D2). */
export const XERO_AUTO_SEND_MAP_SUPPLIERS_CTA = 'Map suppliers'

export const XERO_AUTO_SEND_MAP_SUPPLIERS_HINT =
  'Auto-send is ON. No suppliers have a default Xero account and contact yet — map them under Your Suppliers or OMS will not send bills.'

/**
 * Same zero-defaults gate as the Invoices banner — Integrations shows a
 * primary button so mapping isn’t easy to miss.
 */
export function shouldShowXeroMapSuppliersCta({
  xeroConnected,
  xeroExpired,
  autoSendEnabled,
  mappedSupplierCount = 0,
  activeSupplierCount = 0,
} = {}) {
  // Integrations CTA stays zero-defaults only; Invoices banner also covers
  // partial unmapped via getXeroAutoSendBannerState.showMapSuppliersCta.
  return (
    Boolean(xeroConnected) &&
    !xeroExpired &&
    Boolean(autoSendEnabled) &&
    Number(activeSupplierCount || 0) > 0 &&
    Number(mappedSupplierCount || 0) === 0
  )
}

/**
 * Single Xero status strip on Invoices — discoverability only (no switch).
 * Integrations / GR own the setting; this callout points users there.
 * One banner, state swapped by priority. Venue-level hygiene only;
 * per-invoice supplier gaps stay as post-reconcile toasts.
 *
 * Returns null when Xero is not connected (Integrations owns "connect").
 */
export function getXeroAutoSendBannerState({
  xeroConnected,
  xeroExpired,
  autoSendEnabled,
  mappedSupplierCount = 0,
  activeSupplierCount = 0,
} = {}) {
  if (!xeroConnected) return null

  if (xeroExpired) {
    return {
      state: XERO_BANNER_STATE.EXPIRED,
      tone: 'warning',
      title: 'Xero connection expired',
      detail: 'Auto-send can’t run — reconnect under Integrations.',
      closable: false,
      showSuppliersLink: false,
      showMapSuppliersCta: false,
    }
  }

  const unmappedSupplierCount = Math.max(
    0,
    Number(activeSupplierCount || 0) - Number(mappedSupplierCount || 0),
  )

  if (autoSendEnabled && unmappedSupplierCount > 0) {
    const supplierNoun = unmappedSupplierCount === 1 ? 'supplier' : 'suppliers'
    return {
      state: XERO_BANNER_STATE.NO_SUPPLIER_DEFAULTS,
      tone: 'warning',
      title: `${unmappedSupplierCount} ${supplierNoun} unmapped for Xero`,
      detail:
        mappedSupplierCount === 0
          ? 'Auto-send is ON, but OMS can’t send until suppliers have a mapped Xero account and contact.'
          : 'OMS only sends bills for suppliers with a mapped Xero account and contact.',
      closable: true,
      showSuppliersLink: true,
      showMapSuppliersCta: true,
      unmappedSupplierCount,
    }
  }

  if (!autoSendEnabled) {
    return {
      state: XERO_BANNER_STATE.OFF,
      tone: 'info',
      title: 'Auto-send to Xero is OFF',
      detail: 'Use the paper-plane on Goods Receipting when you need a bill.',
      closable: true,
      showSuppliersLink: false,
      showMapSuppliersCta: false,
    }
  }

  return {
    state: XERO_BANNER_STATE.ON,
    tone: 'info',
    title: 'Auto-send to Xero is ON',
    detail:
      'Reconciled invoices send only when the supplier has a mapped Xero account and contact.',
    closable: true,
    showSuppliersLink: true,
    showMapSuppliersCta: false,
  }
}

export function getXeroAutoSendFailureNotice(
  errorMessage,
  completedActionLabel = 'Reconciled',
) {
  const fallbackDescription =
    completedActionLabel === 'Attached'
      ? 'The invoice was attached, but sending the bill to Xero failed. Use the Send to Xero (paper-plane) action on Goods Receipting to retry.'
      : XERO_AUTO_SEND_FAILURE_RETRY

  return {
    message: `${completedActionLabel} — Xero send failed`,
    description: errorMessage
      ? `${errorMessage} Use the Send to Xero (paper-plane) action on Goods Receipting to retry.`
      : fallbackDescription,
  }
}

/**
 * Apply the post-reconcile Xero toast matrix.
 * Returns whether the short “sent” success toast was shown so callers can skip
 * a second generic reconcile success toast.
 */
export function toastPostReconcileXeroAutoSend({
  autoSendResult,
  venue = {},
  venueSupplier = {},
  order = {},
  error,
  completedActionLabel = 'Reconciled',
} = {}) {
  if (error) {
    const notice = getXeroAutoSendFailureNotice(
      error?.message,
      completedActionLabel,
    )
    notification.warning({
      ...notice,
      duration: 8,
    })
    return { showedSentSuccess: false }
  }

  if (autoSendResult?.sent) {
    notification.success({
      ...getXeroAutoSendSuccessNotice(completedActionLabel),
      duration: 5,
    })
    return { showedSentSuccess: true }
  }

  const autoSendEnabled = readXeroAutoSendEnabled(venue)
  const skipReason = autoSendResult?.reason

  // A2 — intentional quiet: venue OFF, supplier opt-out, already sent.
  if (
    isXeroAutoSendQuietSkip({
      venue,
      order,
      skipReason,
    })
  ) {
    return { showedSentSuccess: false }
  }

  // A1 — odd skips that previously fell through silently when gates looked fine.
  const oddNotice = getXeroAutoSendOddSkipNotice(
    skipReason,
    completedActionLabel,
  )
  if (oddNotice) {
    notification.warning({
      ...oddNotice,
      duration: 8,
    })
    return { showedSentSuccess: false }
  }

  // Classic eligibility skips (expired / not connected / missing defaults).
  const notice = getXeroAutoSendSkipNotice({
    autoSendEnabled,
    xeroConnected: Boolean(venue?.xeroConnection?.accessToken),
    xeroExpired: Boolean(venue?.xeroConnectionExpired),
    hasSupplierDefaults: hasXeroSupplierDefaults(venueSupplier),
    alreadySentToXero: Boolean(order?.sentToXero || order?.xeroInvoiceId),
    completedActionLabel,
  })
  if (notice) {
    notification.warning({
      ...notice,
      duration: 8,
    })
  }
  return { showedSentSuccess: false }
}
